import React, { useState } from 'react';
import { connect } from 'react-redux';
import { Actions as PROFILEACTIONS } from '../store/actions/profileActions';
import { Actions as ADMINACTIONS } from '../store/actions/adminActions';
import { Actions as AUTHACTIONS } from '../store/actions/authenticationActions';
import { toggleAlertMessage } from '../store/action';
import { useForm } from 'react-hook-form';
import Breadcrumb from '../components/Common/Breadcrumb';
import { Label } from '../components/ui/label';
import { Switch } from '../components/ui/switch';
import { Button } from '../components/ui/button';
import { InputField } from '../components/ui/input-field';
import { SelectField } from '../components/ui/select-field';
import { Popover, PopoverContent, PopoverTrigger } from '../components/ui/popover';
import PromoterAgreement from '../components/Modals/PromoterAgreement';
import { CreditCard, Copy, Eye, EyeOff } from 'lucide-react';
import { DATE_FORMAT_OPTIONS, DEFAULT_DATE_FORMAT } from '../components/utils/GlobalFunctions';

const Profile = ({
  orgName,
  stripeAccountId,
  connectStripeAccount,
  profileDetails,
  saving,
  showToggleBtn,
  referralCode,
  registrationLink,
  showStripeConnectBtn,
  setUserPassword,
  saveProfile,
  toggleAlertMessage: showAlert,
}) => {
  const { register, handleSubmit } = useForm();
  const [showPromoterAgreement, setShowPromoterAgreement] = useState(false);
  const [popover, setPopover] = useState(null);
  const [loading, setLoading] = useState(false);
  const [oldPassword, setOldPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [passwordLoading, setPasswordLoading] = useState(false);
  const [showOldPassword, setShowOldPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);

  const { firstName, lastName, email } = profileDetails;

  const onSubmit = async (data) => {
    if (saving) return;
  };

  return (
    <div className='max-w-6xl mx-auto'>
      <Breadcrumb items={[{ label: 'Profile', link: '/profile' }]} />

      <div className='row font-body'>
        <div className='col-lg-12'>
          <section className='panel'>
            <header className='panel-heading font-body'>
              <span>Profile</span>
            </header>
            <div className='panel-body'>
              <div className='position-center'>
                <form onSubmit={handleSubmit(onSubmit)}>
                  <InputField label='First name *' id='firstName' defaultValue={firstName} disabled {...register('firstName', { required: true })} />
                  <InputField label='Last name *' id='lastName' defaultValue={lastName} disabled {...register('lastName', { required: true })} />
                  <InputField label='Organisation' id='company' defaultValue={orgName} disabled {...register('company', { required: true })} />
                  <InputField label='Email address *' id='email' value={email} disabled required />
                  <SelectField
                    label='Date format'
                    options={DATE_FORMAT_OPTIONS}
                    value={profileDetails?.dateFormat ?? DEFAULT_DATE_FORMAT}
                    onChange={(value) => value != null && saveProfile({ ...profileDetails, dateFormat: value })}
                    placeholder='Select date format'
                    hint='Used for dates across the app (e.g. Usage, Reports).'
                    className='max-w-[320px]'
                  />
                  {showToggleBtn && (
                    <div className='flex items-center gap-2'>
                      <Switch id='show-promoter' checked={showPromoterAgreement} onCheckedChange={setShowPromoterAgreement} />
                      <Label htmlFor='show-promoter'>Show Promoter Agreement</Label>
                    </div>
                  )}
                  {referralCode && (
                    <div className='flex items-end gap-2'>
                      <InputField label='Registration Link *' value={registrationLink} disabled required className='flex-1' />
                      <Popover open={!!popover} onOpenChange={(o) => !o && setPopover(null)}>
                        <PopoverTrigger asChild>
                          <Button
                            type='button'
                            variant='ghost'
                            size='icon'
                            className='shrink-0'
                            onClick={() => {
                              navigator.clipboard.writeText(registrationLink);
                              setPopover(true);
                              setTimeout(() => setPopover(null), 1000);
                            }}>
                            <Copy size={16} strokeWidth={1.5} />
                          </Button>
                        </PopoverTrigger>
                        <PopoverContent align='center' className='p-2 text-white font-bold bg-primary'>
                          Copied!
                        </PopoverContent>
                      </Popover>
                    </div>
                  )}
                  {showStripeConnectBtn && (
                    <Button
                      onClick={async () => {
                        setLoading(true);
                        await connectStripeAccount();
                        setLoading(false);
                      }}
                      disabled={!!stripeAccountId || loading}
                      loading={loading}>
                      <CreditCard size={16} strokeWidth={1.5} className='mr-2' />
                      {stripeAccountId ? 'Account connected to stripe' : 'Connect Stripe Account'}
                    </Button>
                  )}
                </form>

                <hr style={{ marginTop: 24, marginBottom: 24 }} />
                <header className='panel-heading' style={{ marginBottom: 16 }}>Password update</header>
                <form
                  onSubmit={async (e) => {
                    e.preventDefault();
                    if (!oldPassword.trim() || !newPassword.trim() || passwordLoading) return;
                    setPasswordLoading(true);
                    try {
                      await setUserPassword(oldPassword, newPassword);
                      showAlert(true, 'Password updated successfully.', 'success');
                      setOldPassword('');
                      setNewPassword('');
                    } catch (err) {
                      showAlert(true, err.message || 'Failed to update password.', 'error');
                    } finally {
                      setPasswordLoading(false);
                    }
                  }}>
                  <div className='relative'>
                    <InputField
                      label='Current password'
                      type={showOldPassword ? 'text' : 'password'}
                      value={oldPassword}
                      onChange={(e) => setOldPassword(e.target.value)}
                      placeholder='Enter current password'
                      autoComplete='current-password'
                      disabled={passwordLoading}
                    />
                    <Button
                      type='button'
                      variant='ghost'
                      size='icon'
                      className='absolute right-2 top-9 h-8 w-8'
                      onClick={() => setShowOldPassword((p) => !p)}
                      aria-label={showOldPassword ? 'Hide password' : 'Show password'}>
                      {showOldPassword ? <EyeOff size={16} strokeWidth={1.5} /> : <Eye size={16} strokeWidth={1.5} />}
                    </Button>
                  </div>
                  <div className='relative'>
                    <InputField
                      label='New password'
                      type={showNewPassword ? 'text' : 'password'}
                      value={newPassword}
                      onChange={(e) => setNewPassword(e.target.value)}
                      placeholder='Enter new password'
                      autoComplete='new-password'
                      disabled={passwordLoading}
                    />
                    <Button
                      type='button'
                      variant='ghost'
                      size='icon'
                      className='absolute right-2 top-9 h-8 w-8'
                      onClick={() => setShowNewPassword((p) => !p)}
                      aria-label={showNewPassword ? 'Hide password' : 'Show password'}>
                      {showNewPassword ? <EyeOff size={16} strokeWidth={1.5} /> : <Eye size={16} strokeWidth={1.5} />}
                    </Button>
                  </div>
                  <Button
                    type='submit'
                    disabled={passwordLoading || !oldPassword.trim() || !newPassword.trim()}
                    loading={passwordLoading}>
                    Update password
                  </Button>
                </form>
              </div>
            </div>
          </section>
        </div>
      </div>

      {showPromoterAgreement && <PromoterAgreement setShowPromoterAgreement={setShowPromoterAgreement} />}
    </div>
  );
};

const mapStateToProps = (state) => {
  const isLocalDevelopment = state.isLocalDevelopment;
  const { details: org } = state.organisation || {};
  const { liveDomain, hasPaymentSystem, enableConsultants, stripeAccountId } = state.whiteLabelAdmin || {};
  const { cyfAdmin, whiteLabelAdmin, ...profileDetails } = state.profile?.details;

  const showStripeConnectBtn = hasPaymentSystem && (whiteLabelAdmin || cyfAdmin);

  const referralCode = state.promoter?.referralCode;
  const showToggleBtn = enableConsultants && org?.consultantId === 'NONE' && !referralCode;

  const registrationLink = isLocalDevelopment
    ? `https://localhost:3000/register?referralCode=${referralCode}`
    : `https://${liveDomain}/register?referralCode=${referralCode}`;

  return {
    orgName: org?.name,
    profileDetails,
    registrationLink,
    saving: state.profile?.saving,
    referralCode,
    showToggleBtn,
    stripeAccountId,
    showStripeConnectBtn,
  };
};

const mapDispatchToProps = (dispatch) => ({
  connectStripeAccount: () => dispatch(PROFILEACTIONS.connectStripeAccount()),
  saveProfile: (profile) => dispatch(PROFILEACTIONS.saveProfile(profile)),
  toggleModal: (status, action, item, notAutoClose) =>
    dispatch(ADMINACTIONS.toggleModal(status, action, item, notAutoClose)),
  setUserPassword: (oldPassword, newPassword) =>
    dispatch(AUTHACTIONS.setUserPassword(oldPassword, newPassword)),
  toggleAlertMessage: (show, message, type) => dispatch(toggleAlertMessage(show, message, type)),
});

export default connect(mapStateToProps, mapDispatchToProps)(Profile);
